/**
 * ffai.com (the FF group site) is a separate website managed in Genesis, like
 * AIXC. Its pages live under /ffai in the CMS and in S3 (/ffai/us/..., /ffai/cn/...,
 * /ffai/ar/...) and are served on www.ffai.com with the /ffai prefix removed:
 *   CMS /ffai/us/about  ->  https://www.ffai.com/us/about/
 */
export const FFAI_PATH_PREFIX = '/ffai';

/**
 * The sections every ffai.com page sits in: /ffai/us, /ffai/cn and /ffai/ar
 * (Arabic, with its English pages under /ffai/ar/en, like ff.com's /ar).
 */
export const FFAI_SECTIONS = ['us', 'cn', 'ar'];
const FFAI_SECTION_FILTER = new RegExp(`^/ffai/(${FFAI_SECTIONS.join('|')})(/|$)`);

/** Home pages ranked highest in the sitemap: the site root, each section and /ar/en. */
const FFAI_HOME_PATHS = ['/', ...FFAI_SECTIONS.map((s) => `/${s}`), '/ar/en'];
export const FFAI_DOMAIN = 'https://www.ffai.com';

/** Matches /ffai and /ffai/... but not /ffai-something. */
export const FFAI_PATH_FILTER = /^\/ffai(\/|$)/;

export function isFfaiPath(path) {
  return FFAI_PATH_FILTER.test(path || '');
}

/** Public path on www.ffai.com for a CMS path: /ffai/us/about -> /us/about */
export function toFfaiPublicPath(cmsPath) {
  const normalized = (cmsPath || '').replace(/\/+$/, '');
  return normalized.replace(FFAI_PATH_FILTER, '/').replace(/^\/+/, '/') || '/';
}

/** Full public URL with a trailing slash: /ffai/us/about -> https://www.ffai.com/us/about/ */
export function toFfaiPublicUrl(cmsPath, domain = FFAI_DOMAIN) {
  const base = (domain || '').replace(/\/+$/, '');
  let publicPath = toFfaiPublicPath(cmsPath);
  if (!publicPath.endsWith('/')) {
    publicPath += '/';
  }
  return `${base}${publicPath}`;
}

/** Sitemap priority: the section homepages (/us, /cn, /ar, /ar/en) rank highest. */
export function getFfaiPagePriority(cmsPath) {
  const publicPath = toFfaiPublicPath(cmsPath);
  return FFAI_HOME_PATHS.includes(publicPath) ? 1 : 0.8;
}

/**
 * Create/copy page check for ffai.com paths (category path + slug). Returns an
 * error message, or '' when the path is fine or not an ffai.com path.
 * Pages must sit under a section: /ffai/us, /ffai/cn or /ffai/ar. Each section's
 * home page goes in its category (e.g. FFAI.COM > US) with an empty slug; a page
 * named us/cn/ar directly under FFAI.COM would take that path and block creating
 * the category.
 */
export function getFfaiPathError(categoryPath, slug) {
  const fullPath = slug ? `${categoryPath || ''}/${slug}` : (categoryPath || '');
  if (!isFfaiPath(fullPath)) {
    return '';
  }

  if (categoryPath === FFAI_PATH_PREFIX && FFAI_SECTIONS.includes(slug || '')) {
    return `Create this page in the FFAI.COM > ${slug.toUpperCase()} category with an empty slug.`;
  }

  if (FFAI_SECTION_FILTER.test(fullPath)) {
    return '';
  }

  return 'FFAI pages must include /us/, /cn/ or /ar/. (e.g., FFAI.COM > US, FFAI.COM > CN or FFAI.COM > AR)';
}
